/**
 * Form-binding review queue — approve / reject pending case_subtype_form_bindings.
 * Surfaces form_number_drift warnings for validated bindings.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  listFormBindings,
  approveFormBinding,
  rejectFormBinding,
  type CaseSubtypeFormBinding,
  type CaseSubtypeLifecycle,
  LIFECYCLE_COLORS,
  LIFECYCLE_LABELS,
} from './caseSubtypeTaxonomyApi';

interface Props {
  stateCode: string;
  /** Coverage-tab seed: override the default 'pending' filter on first mount. */
  initialStatus?: CaseSubtypeLifecycle | 'all';
  /** Coverage-tab drilldown seed: filter to a specific canonical_key. */
  initialCanonicalKey?: string;
  /** Coverage-tab drilldown seed: show only validated bindings with form_number drift. */
  driftOnly?: boolean;
  /** Coverage-tab drilldown seed: scroll + flash this row into view. */
  highlightId?: string;
  onChanged: () => void;
}

const STATUS_OPTIONS: Array<{ value: CaseSubtypeLifecycle | 'all'; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'validated', label: 'Validated' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' },
];

const FormBindingReviewQueue: React.FC<Props> = ({
  stateCode,
  initialStatus,
  initialCanonicalKey,
  driftOnly = false,
  highlightId,
  onChanged,
}) => {
  const [status, setStatus] = useState<CaseSubtypeLifecycle | 'all'>(initialStatus ?? 'pending');
  const [canonicalFilter] = useState<string | undefined>(initialCanonicalKey);
  const [rows, setRows] = useState<CaseSubtypeFormBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { form_bindings } = await listFormBindings({
        status,
        state_code: stateCode || undefined,
        canonical_key: canonicalFilter,
        has_drift: driftOnly || undefined,
        limit: 200,
      });
      setRows(form_bindings);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load form bindings');
    } finally {
      setLoading(false);
    }
  }, [status, stateCode, canonicalFilter, driftOnly]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!highlightId || loading) return;
    const el = document.getElementById(`binding-row-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, loading, rows]);

  const handleApprove = async (row: CaseSubtypeFormBinding) => {
    setBusyId(row.id);
    try {
      await approveFormBinding(row.id, { review_notes: notes[row.id] || undefined });
      onChanged();
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (row: CaseSubtypeFormBinding) => {
    const n = notes[row.id] || '';
    if (!n.trim()) {
      setError('Reject requires review notes.');
      return;
    }
    setBusyId(row.id);
    try {
      await rejectFormBinding(row.id, { review_notes: n });
      onChanged();
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-3" style={{ animation: 'fadeSlideIn 300ms ease-out forwards' }}>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-gray-900/50 rounded-lg p-1">
          {STATUS_OPTIONS.map((o) => {
            const active = o.value === status;
            return (
              <button
                key={o.value}
                onClick={() => setStatus(o.value)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  active
                    ? 'bg-white dark:bg-gray-700 shadow-sm text-violet-700 dark:text-violet-300 ring-1 ring-violet-500/20'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        {canonicalFilter && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
            canonical: {canonicalFilter}
          </span>
        )}
        {driftOnly && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            form-number drift only
          </span>
        )}
        <button
          onClick={reload}
          className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowPathIcon className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-violet-500 border-t-transparent" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No form bindings match the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2 text-left">Scope</th>
                <th className="px-3 py-2 text-left">Canonical</th>
                <th className="px-3 py-2 text-left">Form</th>
                <th className="px-3 py-2 text-left">Relation</th>
                <th className="px-3 py-2 text-left">Resolution</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Notes</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {rows.map((b) => (
                <tr
                  key={b.id}
                  id={`binding-row-${b.id}`}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-900/30 ${
                    highlightId === b.id ? 'ring-2 ring-violet-400 ring-inset bg-violet-50/40 dark:bg-violet-900/10' : ''
                  }`}
                >
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {b.jurisdiction_code || b.state_code || '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-200">
                    {b.canonical_key}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {b.form_number || <span className="text-gray-400">no number</span>}
                    </div>
                    {b.form_name && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                        {b.form_name}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">{b.relation_type}</td>
                  <td className="px-3 py-2 text-xs">
                    {b.form_resolution_status === 'court_form_deleted' ? (
                      <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-300">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                        deleted
                      </span>
                    ) : (
                      <span className="text-gray-600 dark:text-gray-300">{b.form_resolution_status}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${LIFECYCLE_COLORS[b.lifecycle_status]}`}
                    >
                      {LIFECYCLE_LABELS[b.lifecycle_status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {b.lifecycle_status === 'pending' ? (
                      <input
                        type="text"
                        placeholder="Reviewer note…"
                        value={notes[b.id] || ''}
                        onChange={(e) =>
                          setNotes((prev) => ({ ...prev, [b.id]: e.target.value }))
                        }
                        className="w-40 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-violet-500"
                      />
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {b.lifecycle_status === 'pending' ? (
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          disabled={busyId === b.id}
                          onClick={() => handleApprove(b)}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white px-2 py-1 text-xs font-medium disabled:opacity-50"
                        >
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          Approve
                        </button>
                        <button
                          disabled={busyId === b.id}
                          onClick={() => handleReject(b)}
                          className="inline-flex items-center gap-1 rounded-md bg-rose-600 hover:bg-rose-500 text-white px-2 py-1 text-xs font-medium disabled:opacity-50"
                        >
                          <XCircleIcon className="h-3.5 w-3.5" />
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FormBindingReviewQueue;
